import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Table, Button, Alert, Form, Card, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchBids, deleteBid, clearError } from '../redux/slices/bidsSlice';
import { fetchCategories } from '../redux/slices/categoriesSlice';

const Bids: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items: bids, loading, error } = useAppSelector(s => s.bids);
  const { byContentType } = useAppSelector(s => s.categories);
  const bidCategories = byContentType['bids'] || [];

  const [filterCategory, setFilterCategory] = useState('');
  const [localError, setLocalError]          = useState('');

  useEffect(() => {
    dispatch(fetchCategories({ applies_to: 'bids' }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBids(filterCategory));
  }, [dispatch, filterCategory]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this bid?')) return;
    const result = await dispatch(deleteBid(id));
    if (deleteBid.rejected.match(result)) {
      setLocalError(result.payload as string);
    }
  };

  const formatDate = (date: string) =>
    date ? new Date(date).toLocaleDateString() : '-';

  const isClosingSoon = (closingDate: string) => {
    const days = Math.ceil((new Date(closingDate).getTime() - Date.now()) / 86400000);
    return days <= 7 && days >= 0;
  };

  const isClosed = (closingDate: string) =>
    new Date(closingDate) < new Date();

  const getCategoryName = (slug: string) =>
    bidCategories.find(c => c.slug === slug)?.name || slug;

  const displayError = localError || error;

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#00579a', fontWeight: 600, fontSize: '28px', margin: 0 }}>
            Bids Management
          </h1>
          <Link
            to="/bids/new"
            className="btn"
            style={{
              backgroundColor: '#00579a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 20px',
              fontWeight: 500,
              textDecoration: 'none',
              boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
            }}
          >
            + Add New Bid
          </Link>
        </div>

        {/* Error */}
        {displayError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => { setLocalError(''); dispatch(clearError()); }}
            style={{ borderRadius: '6px', border: '1px solid #ffccc7', backgroundColor: '#fff2f0' }}
          >
            {displayError}
          </Alert>
        )}

        {/* Filter */}
        <Card style={{ border: 'none', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: '1.5rem' }}>
          <Card.Body style={{ padding: '16px 20px' }}>
            <Row className="g-2 align-items-end">
              <Col xs={12} sm={5} md={4}>
                <Form.Label style={{ fontWeight: 500, color: '#262626', fontSize: '13px', marginBottom: '6px' }}>
                  Filter by Category
                </Form.Label>
                <Form.Select
                  value={filterCategory}
                  onChange={e => setFilterCategory(e.target.value)}
                  style={{ borderRadius: '6px', border: '1px solid #d9d9d9', fontSize: '14px' }}
                >
                  <option value="">All Categories</option>
                  {bidCategories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </Form.Select>
              </Col>
              {filterCategory && (
                <Col xs="auto">
                  <Button
                    variant="link"
                    onClick={() => setFilterCategory('')}
                    style={{ color: '#8c8c8c', padding: '6px 0', fontSize: '13px' }}
                  >
                    Clear
                  </Button>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card style={{ border: 'none', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <Card.Body style={{ padding: 0 }}>
            {loading ? (
              <div className="text-center" style={{ padding: '60px 0' }}>
                <Spinner animation="border" style={{ color: '#00579a', width: '40px', height: '40px' }} />
                <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading bids...</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table hover style={{ margin: 0 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th style={th}>Title</th>
                      <th style={th}>Category</th>
                      <th style={th}>Closing Date</th>
                      <th style={th}>Status</th>
                      <th style={th}>Documents</th>
                      <th style={th}>Created</th>
                      <th style={th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bids.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center" style={{ padding: '60px 20px', color: '#8c8c8c', fontSize: '15px' }}>
                          No bids found. Click "Add New Bid" to create one.
                        </td>
                      </tr>
                    ) : (
                      bids.map(bid => (
                        <tr key={bid.id}>
                          {/* Title */}
                          <td style={{ ...td, fontWeight: 500, color: '#262626' }}>
                            {bid.title}
                          </td>

                          {/* Category */}
                          <td style={td}>
                            {bid.category ? (
                              <Badge style={{
                                backgroundColor: '#e6f4ff',
                                color: '#0050b3',
                                fontWeight: 500,
                                padding: '4px 10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                textTransform: 'capitalize',
                              }}>
                                {getCategoryName(bid.category)}
                              </Badge>
                            ) : (
                              <span style={{ color: '#bfbfbf', fontSize: '13px' }}>—</span>
                            )}
                          </td>

                          {/* Closing Date */}
                          <td style={td}>
                            {formatDate(bid.closing_date)}
                            {isClosingSoon(bid.closing_date) && (
                              <Badge style={{
                                backgroundColor: '#fff7e6',
                                color: '#d46b08',
                                fontWeight: 500,
                                padding: '3px 7px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                marginLeft: '8px',
                              }}>
                                Soon
                              </Badge>
                            )}
                          </td>

                          {/* Status */}
                          <td style={td}>
                            <Badge style={{
                              backgroundColor: isClosed(bid.closing_date) ? '#fff1f0' : '#f6ffed',
                              color:           isClosed(bid.closing_date) ? '#cf1322' : '#389e0d',
                              fontWeight: 500,
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '12px',
                            }}>
                              {isClosed(bid.closing_date) ? 'Closed' : 'Open'}
                            </Badge>
                          </td>

                          {/* Documents */}
                          <td style={td}>
                            <Badge style={{
                              backgroundColor: '#f0f0f0',
                              color: '#595959',
                              fontWeight: 500,
                              padding: '4px 10px',
                              borderRadius: '4px',
                            }}>
                              {bid.documents?.length || 0} files
                            </Badge>
                          </td>

                          {/* Created */}
                          <td style={td}>{formatDate(bid.created_at)}</td>

                          {/* Actions */}
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <Link
                              to={`/bids/edit/${bid.id}`}
                              className="btn btn-sm me-2"
                              style={{
                                backgroundColor: 'white',
                                color: '#00579a',
                                border: '1px solid #00579a',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                textDecoration: 'none',
                              }}
                            >
                              Edit
                            </Link>
                            <Button
                              size="sm"
                              onClick={() => handleDelete(bid.id)}
                              style={{
                                backgroundColor: 'white',
                                color: '#ff4d4f',
                                border: '1px solid #ff4d4f',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                fontSize: '12px',
                                fontWeight: 500,
                              }}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const th: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#595959',
  borderBottom: '1px solid #f0f0f0',
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#595959',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle',
};

export default Bids;